import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Filter,
	Package,
	Search,
	SlidersHorizontal,
	Store,
	Tag,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { PageHeader } from "../components/layout/PageHeader";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ProductRow {
	id: number;
	gtin: string;
	name: string;
	url: string | null;
	lowestPrice: number | null;
	highestPrice: number | null;
	primaryImage: string | null;
	brand: string | null;
	color: string | null;
	merchantCount: number;
}

interface ProductPage {
	items: ProductRow[];
	page: number;
	pageSize: number;
	hasMore: boolean;
}

interface BrandOption {
	brand: string;
	productCount: number;
}

const PAGE_SIZE = 24;

// ---------------------------------------------------------------------------
// Brand filter
// ---------------------------------------------------------------------------
const BrandFilter = ({
	selected,
	onSelect,
}: {
	selected: string;
	onSelect: (brand: string) => void;
}) => {
	const [open, setOpen] = useState(true);
	const [search, setSearch] = useState("");
	const [brands, setBrands] = useState<BrandOption[]>([]);

	useEffect(() => {
		let cancelled = false;
		const timer = setTimeout(async () => {
			try {
				const { data } = await api.products.brands.get({
					query: { q: search.trim(), limit: "30" },
				});
				if (!cancelled && data)
					setBrands((data as unknown as { items: BrandOption[] }).items);
			} catch {
				if (!cancelled) setBrands([]);
			}
		}, 250);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [search]);

	return (
		<div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
			>
				<span className="flex items-center gap-2">
					<Tag className="w-4 h-4 text-slate-400" />
					Brand
				</span>
				<ChevronDown
					className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</button>
			{open && (
				<div className="px-4 pb-4 space-y-2">
					<input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search brands…"
						className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-primary/50"
					/>
					{selected && (
						<button
							type="button"
							onClick={() => onSelect("")}
							className="flex items-center gap-1 text-xs text-primary hover:underline cursor-pointer"
						>
							<X className="w-3 h-3" />
							Clear “{selected}”
						</button>
					)}
					<ul className="space-y-1 max-h-72 overflow-y-auto">
						{brands.map((option) => (
							<li key={option.brand}>
								<button
									type="button"
									onClick={() =>
										onSelect(option.brand === selected ? "" : option.brand)
									}
									className={`flex items-center justify-between w-full px-2 py-1 rounded-lg text-xs transition-colors cursor-pointer ${
										option.brand === selected
											? "bg-primary/10 text-primary font-semibold"
											: "text-slate-600 hover:bg-slate-50"
									}`}
								>
									<span className="truncate">{option.brand}</span>
									<span className="text-slate-400 tabular-nums">
										{option.productCount}
									</span>
								</button>
							</li>
						))}
						{brands.length === 0 && (
							<li className="text-xs text-slate-400 px-2 py-1">
								No brands found
							</li>
						)}
					</ul>
				</div>
			)}
		</div>
	);
};

// ---------------------------------------------------------------------------
// Product card
// ---------------------------------------------------------------------------
const ProductCard = ({ product }: { product: ProductRow }) => {
	const priceRange =
		product.lowestPrice != null &&
		product.highestPrice != null &&
		product.lowestPrice !== product.highestPrice
			? `${product.lowestPrice.toLocaleString("da-DK")} – ${product.highestPrice.toLocaleString("da-DK")}`
			: product.lowestPrice != null
				? product.lowestPrice.toLocaleString("da-DK")
				: null;

	return (
		<Link
			to={`/products/${product.id}`}
			className="group flex flex-col h-full bg-white rounded-xl border border-slate-200 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200 overflow-hidden cursor-pointer"
		>
			<div className="relative aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
				{product.primaryImage ? (
					<img
						src={product.primaryImage}
						alt={product.name}
						className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
					/>
				) : (
					<Package className="w-12 h-12 text-slate-300" />
				)}
			</div>

			<div className="flex flex-col flex-1 p-4 space-y-2">
				<h3 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug">
					{product.name}
				</h3>
				<p className="text-[11px] text-slate-400 font-mono">ID: {product.id}</p>
				{product.brand && (
					<p className="text-xs text-slate-500 flex items-center gap-1">
						<Tag className="w-3 h-3" />
						{product.brand}
					</p>
				)}
				{product.color && (
					<p className="text-[11px] text-slate-400">{product.color}</p>
				)}
				<div className="flex items-center justify-between pt-1 mt-auto">
					{priceRange && (
						<span className="text-sm font-bold text-slate-800">
							DKK {priceRange}
						</span>
					)}
					<span
						className={`text-[11px] flex items-center gap-1 ${
							product.merchantCount === 0 ? "text-amber-600" : "text-slate-400"
						}`}
					>
						<Store className="w-3 h-3" />
						{product.merchantCount} merchant{product.merchantCount !== 1 && "s"}
					</span>
				</div>
			</div>
		</Link>
	);
};

// ---------------------------------------------------------------------------
// Products page
// ---------------------------------------------------------------------------
const Products = () => {
	const [filtersOpen, setFiltersOpen] = useState(true);
	const [search, setSearch] = useState("");
	const [brand, setBrand] = useState("");
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<ProductPage | null>(null);
	const [loading, setLoading] = useState(true);
	const [elapsedMs, setElapsedMs] = useState<number | null>(null);

	// Reading the catalogue from MySQL rather than the search index means every
	// row is reachable — including the products the indexer drops for having no
	// active priced offer, which are usually the ones an admin is looking for.
	const load = useCallback(async () => {
		setLoading(true);
		const startedAt = performance.now();
		try {
			const query: Record<string, string> = {
				page: String(page),
				pageSize: String(PAGE_SIZE),
			};
			if (search.trim()) query.q = search.trim();
			if (brand) query.brand = brand;

			const { data } = await api.products.get({ query });
			setResult(data ? (data as unknown as ProductPage) : null);
		} catch {
			setResult(null);
		} finally {
			setElapsedMs(Math.round(performance.now() - startedAt));
			setLoading(false);
		}
	}, [page, search, brand]);

	// Debounced so typing does not fire a LIKE scan per keystroke.
	useEffect(() => {
		const timer = setTimeout(load, 250);
		return () => clearTimeout(timer);
	}, [load]);

	const onSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const onBrandChange = (value: string) => {
		setBrand(value);
		setPage(1);
	};

	const items = result?.items ?? [];

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Shop · Products"
				title="Products."
				qualifier="The full catalogue, searchable."
				description={
					elapsedMs !== null && !loading
						? `${items.length} shown · ${elapsedMs} ms`
						: "Search by name, EAN or product id."
				}
			/>

			{/* Search bar + filter toggle */}
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="relative flex-1 max-w-xl">
					<Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
					<input
						value={search}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder="Search products by name, EAN or ID…"
						className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10 transition-all"
					/>
					{search && (
						<button
							type="button"
							onClick={() => onSearchChange("")}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
						>
							<X className="w-4 h-4" />
						</button>
					)}
				</div>

				<div className="flex items-center gap-2 flex-wrap">
					<button
						type="button"
						onClick={() => setFiltersOpen(!filtersOpen)}
						className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border cursor-pointer select-none ${
							filtersOpen
								? "bg-slate-800 text-white border-slate-800"
								: "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
						}`}
					>
						<SlidersHorizontal className="w-3.5 h-3.5" />
						Filters
					</button>
				</div>
			</div>

			{/* Main content: sidebar + grid */}
			<div className="flex gap-6">
				{filtersOpen && (
					<aside className="w-64 shrink-0 space-y-4">
						<div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
							<Filter className="w-4 h-4 text-slate-400" />
							Filters
						</div>
						<BrandFilter selected={brand} onSelect={onBrandChange} />
					</aside>
				)}

				<div className="flex-1 min-w-0">
					{loading ? (
						<div className="flex items-center justify-center py-20">
							<div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
						</div>
					) : items.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-20 text-slate-400">
							<Package className="w-16 h-16 mb-4 text-slate-300" />
							<p className="text-lg font-semibold text-slate-500">
								No products found
							</p>
							<p className="text-sm">Try adjusting your search or filters</p>
						</div>
					) : (
						<ul className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
							{items.map((product) => (
								<li key={product.id} className="h-full">
									<ProductCard product={product} />
								</li>
							))}
						</ul>
					)}

					{/* Pagination. No page numbers: the endpoint deliberately skips the
					    COUNT that a filtered search would make a full table scan. */}
					{(page > 1 || result?.hasMore) && (
						<div className="mt-8 flex items-center justify-center gap-3">
							<button
								type="button"
								disabled={page === 1 || loading}
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								className="inline-flex items-center gap-1 px-3 h-9 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
							>
								<ChevronLeft className="w-4 h-4" />
								Previous
							</button>
							<span className="text-sm text-slate-500 tabular-nums">
								Page {page}
							</span>
							<button
								type="button"
								disabled={!result?.hasMore || loading}
								onClick={() => setPage((p) => p + 1)}
								className="inline-flex items-center gap-1 px-3 h-9 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
							>
								Next
								<ChevronRight className="w-4 h-4" />
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default Products;
